import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/index.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { TenantProvider } from "@/context/TenantContext";
import { Toaster } from "@/components/ui/sonner";

import CustomerProjects from "@/pages/customer/Projects";
import CustomerProjectDetail from "@/pages/customer/ProjectDetail";
import ProfilePage from "@/pages/Profile";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/Login";
import BrandPreview from "@/pages/BrandPreview";
import AdminDashboard from "@/pages/admin/Dashboard";
import CustomerDashboard from "@/pages/customer/Dashboard";
import LeadsPage from "@/pages/admin/Leads";
import LeadDetail from "@/pages/admin/LeadDetail";
import CustomersPage from "@/pages/admin/Customers";
import CustomerDetail from "@/pages/admin/CustomerDetail";
import TasksPage from "@/pages/admin/Tasks";
import TeamPage from "@/pages/admin/Team";
import ResourcePage from "@/pages/admin/Resource";
import QuotesPage from "@/pages/shared/Quotes";
import InvoicesPage from "@/pages/shared/Invoices";
import ProjectsPage from "@/pages/shared/Projects";
import ProjectDetail from "@/pages/shared/ProjectDetail";
import ProductsPage from "@/pages/admin/Products";
import RevenuePage from "@/pages/admin/Revenue";
import MySoftwarePage from "@/pages/customer/Software";
import RoadmapPage from "@/pages/shared/Roadmap";
import AppsPage from "@/pages/shared/Apps";
import TicketsPage from "@/pages/shared/Tickets";
import SettingsPage from "@/pages/Settings";
import TvDashboard from "@/pages/admin/TvDashboard";

function Protected({ children }) {
  const { user, booting } = useAuth();
  if (booting) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3 text-[hsl(var(--text-muted))]">
          <div className="w-4 h-4 rounded-full border-2 border-[hsl(var(--primary))] border-t-transparent animate-spin" />
          <span className="text-[13px]">Loading…</span>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  if (user?.role === "customer") return <CustomerDashboard />;
  return <AdminDashboard />;
}

function App() {
  return (
    <ThemeProvider>
      <TenantProvider>
        <AuthProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/brand-preview" element={<BrandPreview />} />
            <Route path="/tv" element={<Protected><TvDashboard /></Protected>} />
            <Route path="*" element={
              <Protected>
                <Layout>
                  <Routes>
                    <Route path="/" element={<RoleHome />} />
                    {/* Admin routes */}
                    <Route path="/leads" element={<LeadsPage />} />
                    <Route path="/leads/:id" element={<LeadDetail />} />
                    <Route path="/customers" element={<CustomersPage />} />
                    <Route path="/customers/:id" element={<CustomerDetail />} />
                    <Route path="/quotes" element={<QuotesPage />} />
                    <Route path="/invoices" element={<InvoicesPage scope="admin" />} />
                    <Route path="/projects" element={<ProjectsPage />} />
                    <Route path="/projects/:id" element={<ProjectDetail />} />
                    <Route path="/apps" element={<AppsPage />} />
                    <Route path="/products" element={<ProductsPage />} />
                    <Route path="/revenue" element={<RevenuePage />} />
                    <Route path="/roadmap" element={<RoadmapPage />} />
                    <Route path="/tasks" element={<TasksPage />} />
                    <Route path="/tickets" element={<TicketsPage />} />
                    <Route path="/team" element={<TeamPage />} />
                    <Route path="/resource" element={<ResourcePage />} />
                    {/* Customer routes (aliases. Same shared pages) */}
                    <Route path="/my-apps" element={<AppsPage />} />
                    <Route path="/my-software" element={<MySoftwarePage />} />
                    <Route path="/my-roadmap" element={<RoadmapPage />} />
                    <Route path="/my-invoices" element={<InvoicesPage scope="customer" />} />
                    <Route path="/my-quotes" element={<QuotesPage />} />
                    <Route path="/my-projects" element={<CustomerProjects />} />
                    <Route path="/my-projects/:id" element={<CustomerProjectDetail />} />
                    <Route path="/my-tickets" element={<TicketsPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Layout>
              </Protected>
            } />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        </AuthProvider>
      </TenantProvider>
    </ThemeProvider>
  );
}

export default App;
